import React, { useEffect, useState } from "react";
import { Select, Table } from "antd";
import { ColumnType, TableProps } from "antd/lib/table";
import { TableRowSelection } from "antd/lib/table/interface";
import { TFunction } from "i18next";
import _, { unset } from "lodash";
import { FieldValues } from "react-hook-form";
import { withTranslation } from "react-i18next";
import PageButtonBox from "../Pagination/PageButtonBox";
import PaginationBox from "../Pagination/PaginationBox";
import "./TableBox.scss";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import Button from "../Button";
import { showText } from "pdf-lib";

interface Record extends Object {
  id?: string;
  _id?: string;
  key?: string | number;
}

export type ColumnsType<RecordType extends Record> = ColumnType<RecordType> & {
  dontShow?: boolean;
  disableEllipsis?: boolean;
};

export type PageData = {
  totalCount: number;
  page: number;
  perPage: number;
};

type Props<RecordType extends Record, TParams extends FieldValues> = {
  onSelect?: (selected: RecordType[]) => void;
  selectedRows?: RecordType[];
  columns: ColumnsType<RecordType>[] | any;
  pageData?: TParams & PageData;
  onChangePage?: (pageData: any, pageSize?: number) => void;
  pageLabel?: string;
  onNextPage?: (id: string) => void;
  enablePage?: string;
  paginationTemplate?: "standard" | "simplified";
  t: TFunction<"translation", undefined>;
} & TableProps<RecordType>;

function TableBox<
  RecordType extends Record = Record,
  TParams extends FieldValues = PageData
>(props: Props<RecordType, TParams>) {
  const {
    dataSource = [],
    onSelect,
    pageLabel,
    onNextPage,
    enablePage,
    pageData,
    onChangePage,
  } = props;
  const [internalPage, setInternalPage] = useState<number>(1);
  const [internalPerPage, setInternalPerPage] = useState<number>(10);
  const isServerSide = !!pageData && typeof onChangePage === "function";

  useEffect(() => {
    if (pageData?.page) setInternalPage(pageData.page);
    if (pageData?.perPage) setInternalPerPage(pageData.perPage);
  }, [pageData?.page, pageData?.perPage]);

  const effectivePage = pageData?.page ?? internalPage;
  const effectivePerPage = pageData?.perPage ?? internalPerPage;
  const effectiveTotal = pageData?.totalCount ?? dataSource.length;

  const handleChangePage = ({
    page,
    perPage,
  }: {
    page: number;
    perPage: number;
  }) => {
    if (onChangePage) {
      onChangePage({ ...(pageData || {}), page, perPage } as TParams &
        PageData);
    } else {
      setInternalPage(page);
      setInternalPerPage(perPage);
    }
  };

  const onSelectChange = (selected: RecordType) => {
    let selectedRows = [...(props.selectedRows || [])];
    const check = selectedRows.find((val) => val.id === selected.id);
    if (check) {
      selectedRows = selectedRows.filter((val) => val.id !== selected.id);
    } else {
      selectedRows.push(selected);
    }
    props.onSelect?.(selectedRows);
  };

  const onSelectAll = (
    selected: boolean,
    _changeRows: RecordType[],
    rows: RecordType[]
  ) => {
    const selectedRowKeys = rows.map((v) => v.id);
    let selectedRows = [...(props.selectedRows || [])];
    if (selected) {
      selectedRows = [...selectedRows, ...(props.dataSource || [])];
      selectedRows = _.uniqBy(selectedRows, (v) => v.id);
    } else {
      selectedRows = _.remove(
        selectedRows,
        (v) => !selectedRowKeys.includes(v.id)
      );
    }
    props.onSelect?.(selectedRows);
  };

  const rowSelection: TableRowSelection<RecordType> = {
    selectedRowKeys:
      (props.selectedRows || []).length > 0
        ? props.selectedRows?.map((val) => val.id || val._id || "")
        : [],
    onSelect: onSelectChange,
    onSelectAll,
    preserveSelectedRowKeys: true,
  };

  const enhancedColumns = (props.columns || [])
    .filter((col: any) => !col.dontShow)
    .map((col: any) => {
      return {
        ...col,
        ellipsis: false,
        width: col.width || 50,
        align: "center",
        onHeaderCell: () => ({
          style: {
            whiteSpace: "nowrap",
            overflow: "visible",
            textOverflow: "clip",
          },
        }),
      };
    });

  const paginatedData = isServerSide
    ? dataSource
    : dataSource.slice(
        (effectivePage - 1) * effectivePerPage,
        effectivePage * effectivePerPage
      );

  return (
    <>
      <div
        className="card-datatable table-responsive"
        style={{
          position: "relative",
          maxHeight: "500px",
          overflow: "auto",
        }}
      >
        <Table
          {...(onSelect && { rowSelection })}
          pagination={false}
          {...props}
          columns={enhancedColumns}
          dataSource={paginatedData.map((data, i) => {
            data.key = data.id || data._id || i;
            return data;
          })}
          tableLayout="auto"
          className="datatables-users table dataTable no-footer dtr-column"
          scroll={{
            x: "max-content",
          }}
        />
        {onNextPage && (
          <div style={{ padding: 20, textAlign: "end" }}>
            <PageButtonBox
              pageLabel={pageLabel}
              enablePage={enablePage}
              dataSource={[...dataSource]}
              onNextPage={onNextPage}
            />
          </div>
        )}
      </div>
      {effectiveTotal > 10 && (
        <PaginationBox
          pageData={{
            page: effectivePage,
            perPage: effectivePerPage,
            totalCount: effectiveTotal,
          }}
          onChangePage={handleChangePage}
        />
      )}
    </>
  );
}

export default withTranslation()(TableBox);
